import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  model,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource
} from '@angular/material/table';
import { Fleet } from '../../../shared/models/fleet/fleet';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FleetService } from '../../../shared/services/fleet.service';
import { Alert, AlertService, AlertType, LinkModule } from '@michelin/theme';
import { MatIconButton } from '@angular/material/button';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { NavigationConfirmService } from '../../../shared/services/navigation-confirm.service';
import {FormArray, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import { NoScrollDirective } from '../../../directives/nx-no-scroll.directive';
import { ContextService } from '../../../shared/services/context.service';
import { IModif } from '../../../shared/models/IModif';
import { NoDataComponent } from '../../../shared/components/no-data/no-data.component';
import moment from 'moment';
import {NgxMaskDirective, provideNgxMask} from "ngx-mask";
import {MAX_VALUE} from "../../../shared/constants/StockConstants";

@Component({
  selector: 'mic-fleet-hour-table',
  imports: [
    LinkModule,
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderRow,
    MatHeaderRowDef,
    MatIcon,
    MatIconButton,
    MatRow,
    MatRowDef,
    MatSort,
    MatSortHeader,
    MatTable,
    TranslatePipe,
    NoScrollDirective,
    NoDataComponent,
    NgxMaskDirective
  ],
  templateUrl: './hour-table.component.html',
  styleUrl: './hour-table.component.scss',
  providers: [provideNgxMask()]
})
export class HourTableComponent implements OnInit, OnChanges, IModif, OnDestroy {
  ///////////////////////////////////////
  /// Common code between FleetTables ///
  ///////////////////////////////////////

  // Have tried to mutualize it but Angular doesn't handle 2wany bind and similar features with generics
  // If you think you can do better and want to refactor please increment the counter below :)
  // Wasted time try to refactor this : 2h

  /**
   * Datatable config
   */
  nextMonthDate: Date = moment().add(1, 'M').toDate();
  currentYear: number = this.nextMonthDate.getFullYear();
  nextMonth = this.nextMonthDate.getMonth() + 1;
  monthsNumbersToDisplay = Array.from({ length: 12 }, (_, i) => ((this.nextMonth - 1 + i) % 12) + 1);
  @ViewChild(MatSort) sort: MatSort;
  dataSource = new MatTableDataSource<Fleet>();
  columns = ['seat', 'model', 'realHours', ...this.monthsNumbersToDisplay.map((m) => m.toString()), 'action'];
  private _pendingDeletions: number[] = [];

  readonly digitMask = '0*';
  readonly digitPatterns = { '0': { pattern: /\d/ } };
  readonly MAX_HOURS = 744;

  /**
   * Search parameters (two-way bound in case of rollback with confirm modal)
   */
  siteId: number;
  year = model<number>(this.currentYear);
  @Input() truckModel: string = '';
  private isRollingBackSearchParams = false;

  /**
   * Property and to know if there are modifications pending and data loading status
   */
  isModificationsPending = model<boolean>(false);
  isLoadingData = model<boolean>(false);
  isSavingData = model<boolean>(false);
  @Output() dataLoaded = new EventEmitter<Fleet[]>();

  ngOnInit() {
    this.contextService.siteId$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((id) => {
      if (id !== null && id !== this.siteId) {
        this.siteId = id;
        this.loadFleetData();
      }
    });

    // Set up custom sorting for the table, as some columns are based on nested properties of the data source
    this.dataSource.sortingDataAccessor = (data, col) => {
      if (col === 'seat') {
        return data.truckSpec.seat;
      } else if (col === 'model') {
        return data.truckSpec.model;
      }
    };
  }

  /**
   * Refresh data on search parameters changes
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes.year && changes.year.firstChange) {
      this.loadFleetData();
      return;
    }
    if (changes.year || changes.mine) {
      if (this.isRollingBackSearchParams) {
        this.isRollingBackSearchParams = false;
        return;
      }
      this.beforeActionWithConfirmation(
        () => {
          this.loadFleetData();
        },
        () => {
          this.isRollingBackSearchParams = true;
          this.year.update(() => changes.year.previousValue);
        }
      );
    }
    if (changes.truckModel) {
      this.dataSource.filter = this.truckModel?.trim().toLowerCase();
    }
  }

  /**
   * Load fleet data from backend
   * @private
   */
  private loadFleetData() {
    if (!this.year() || !this.siteId) return; // Ensure search params are defined, overwise skip loading
    this.isLoadingData.update(() => true);
    this.dataSource.data = [] as Fleet[]; // Clear current data
    this.fleetService
      .getFleetQuantities(this.siteId, this.year(), this.nextMonth)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((fleetData: Fleet[]) => {
        this._pendingDeletions = [];
        this.dataSource.data = fleetData;
        this.dataLoaded.emit(fleetData);
        this.isLoadingData.update(() => false);
        // Reset modifications pending flag, as we just loaded fresh data
        this.isModificationsPending.update(() => false);
        this.dataSource.sort = this.sort;
        this.dataSource.filterPredicate = (data: Fleet, filter: string) => {
          return data.truckSpec.specification.toLowerCase().includes(filter);
        };
        this.dataSource.filter = this.truckModel?.trim().toLowerCase();
        this.buildForm(); // (Re)build the form with the new data
      });
  }

  /**
   * Open a confirmation modal if there are modifications pending before executing an action
   * @param onContinue the action to execute if there are no modifications pending or if the user confirms
   * @param onCancel the action to execute if the user cancels
   * @private
   */
  private beforeActionWithConfirmation(onContinue: () => void, onCancel?: () => void) {
    if (this.isModificationsPending()) {
      this.navigationConfirm.confirmNavigation(onContinue, onCancel);
    } else {
      onContinue();
    }
  }

  /**
   * Compute the year for a given month number based on the current year and displayed months
   * @param month Month number (1-12)
   * @returns Corresponding year
   */
  getYearForMonth(month: number) {
    let index = this.monthsNumbersToDisplay.findIndex((m) => m === month);
    let year = this.year();
    for (let i = 0; i < index; i++) {
      // If bigger month number found before, it means we passed to next year
      if (this.monthsNumbersToDisplay[i] > month) {
        return year + 1;
      }
    }
    return year;
  }

  /**
   * Get CSS class for a month cell based on whether it's editable
   * @param month Month number (1-12)
   * @param year Year (4 digits)
   * @returns CSS class name or undefined
   */
  getMonthClass(month: number, year: number): string {
    if (!this.isEditableMonth(month, year)) return 'column-quantity-gray';
  }

  /**
   * Determine if a month is editable based on current date
   * @param month Month number (1-12)
   * @param year Year (4 digits)
   * @returns True if editable, false otherwise
   */
  isEditableMonth(month: number, year: number): boolean {
    return year > this.currentYear || (month >= this.nextMonth && year === this.currentYear);
  }

  protected deleteFleet(element: Fleet) {
    this.navigationConfirm.confirmModal(
      'fleet.card.table.delete.confirm.title',
      'fleet.card.table.delete.confirm.description',
      () => {
        this._pendingDeletions.push(element.truckStat.truckStatPk);
        this.dataSource.data = this.dataSource.data.filter((f) => f.truckStat.truckStatPk !== element.truckStat.truckStatPk);
        this.dataSource._updateChangeSubscription();
        this.isModificationsPending.update(() => true);
      },
      () => {
        // Cancel action
      }
    );
  }

  saveModifications(withRecalculation: boolean) {
    this.isSavingData.update(() => true);
    this.saveFleets(withRecalculation);
  }

  private saveFleets(withRecalculation: boolean) {
    this.fleetService
      .saveFleets(this.siteId, this.year(), this.nextMonth, this._pendingDeletions, this.dataSource.data)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isModificationsPending.update(() => false);
          this.isSavingData.update(() => false);
          this.loadFleetData();

          this.alertService.showAlert({
            type: AlertType.success,
            title: this.translate.instant('fleet.card.table.save.successTitle')
          } as Alert);

          if (!withRecalculation) {
            return;
          }

          this.fleetService
            .recalculateForecast(this.siteId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => {
                this.alertService.showAlert({
                  type: AlertType.success,
                  title: this.translate.instant('sites.seasonality.alerts.recalculateForecast.title'),
                  content: this.translate.instant('sites.seasonality.alerts.recalculateForecast.description')
                } as Alert);
              },
              error: (err) => {
                this.alertService.showAlert({
                  type: AlertType.critical,
                  title: this.translate.instant('sites.seasonality.alerts.recalculateForecast.errorTitle'),
                  content: err?.error ?? err?.error?.message ?? ''
                } as Alert);
              }
            });
        },
        error: (err) => {
          let errorMsg: string | undefined = undefined;
          if (err?.error?.message) {
            errorMsg = `(${err.error.message})`;
          }

          this.alertService.showAlert({
            type: AlertType.critical,
            title: this.translate.instant('fleet.card.table.save.errorTitle'),
            content: `${this.translate.instant('fleet.card.table.save.errorMsg')} ${errorMsg ?? ''}`
          } as Alert);

          this.isSavingData.update(() => false);
        }
      });
  }

  ////////////////////////////////////
  /// Specific code for Hour Table ///
  ////////////////////////////////////

  form: FormArray; // Dynamic forms
  avgHoursControls: FormGroup[] = []; // Controls to access each row's FormGroup

  constructor(
    private fleetService: FleetService,
    private alertService: AlertService,
    private translate: TranslateService,
    private navigationConfirm: NavigationConfirmService,
    private contextService: ContextService,
    private destroyRef: DestroyRef,
    private fb: FormBuilder // Ajout du FormBuilder
  ) {
    contextService.register(this);
  }

  getModifStatus(): boolean {
    return this.isModificationsPending();
  }

  ngOnDestroy(): void {
    this.contextService.unRegister(this);
  }

  /**
   * Initializes the dynamic form for the hours table.
   * Creates a FormArray where each row is a FormGroup containing a FormControl for each displayed month.
   * The initial values of the FormControls are the hours from fcsts for each month.
   * Automatically synchronizes form changes with the business data (Fleet.fcsts[month].hours).
   */
  private buildForm(): void {
    this.form = this.fb.array(
      this.dataSource.data.map((row: Fleet) =>
        this.fb.group(
          this.monthsNumbersToDisplay.reduce((acc: { [key: string]: FormControl }, month: number) => {
            acc[month.toString()] = new FormControl(row.fcsts[month]?.averageHoursMonth ?? null, [
              Validators.min(0),
              Validators.max(this.MAX_HOURS)
            ]);
            return acc;
          }, {})
        )
      )
    ) as FormArray<FormGroup>;
    this.subscribeAutomaticSynchronizationOfChanges();
    this.avgHoursControls = this.form.controls as FormGroup[];
  }

  /**
   * Automatically synchronize changes from the form to the business data model.
   * When a user modifies a month's hours in a row, all next months in that row are updated to the same value.
   * FormControls are updated without emitting valueChanges events to avoid infinite loops.
   * @private
   */
  private subscribeAutomaticSynchronizationOfChanges() {
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((values: Record<string, any>[]) => {
      values.forEach((rowValues: Record<string, any>, rowIndex: number) => {
        let newValue = -1;
        for (const month of this.monthsNumbersToDisplay) {
          const monthStr = month.toString();
          const monthNum = Number(monthStr);
          const value = rowValues[monthStr];
          let valueNb = Number(value);
          if (Number.isNaN(valueNb)) valueNb = 0;
          const capped = Math.max(0, Math.min(this.MAX_HOURS, valueNb));

          if (!this.dataSource.data[rowIndex].fcsts[monthNum]) return;
          if (newValue === -1 && this.dataSource.data[rowIndex].fcsts[monthNum].averageHoursMonth !== capped) {
            newValue = capped;
            this.dataSource.data[rowIndex].fcsts[monthNum].averageHoursMonth = capped;
            this.updateControlWithoutNotification(rowIndex, monthStr, capped);
            this.isModificationsPending.update(() => true);
          } else if (newValue !== -1) {
            this.dataSource.data[rowIndex].fcsts[monthNum].averageHoursMonth = newValue;
            this.updateControlWithoutNotification(rowIndex, monthStr, newValue);
          }
        }
      });
    });
  }

  onHoursChange(control: FormControl | null): void {
    if (!control) return;

    if (!control.value) {
      control.setValue(0, { emitEvent: false });
    } else if (control.value > this.MAX_HOURS) {
      control.setValue(this.MAX_HOURS, { emitEvent: false });
    }
  }


  /**
   * Update a specific FormControl value without emitting valueChanges event
   * @param rowIndex the row index where the control is located
   * @param month the month (as string) representing the control name
   * @param valueNb the new value to set
   * @private
   */
  private updateControlWithoutNotification(rowIndex: number, month: string, valueNb: number) {
    const control = (this.form.at(rowIndex) as FormGroup).get(month);
    if (control) {
      control.setValue(valueNb, { emitEvent: false });
    }
  }

  getAvgHoursControl(rowIndex: number, month: number): FormControl | null {
    return (this.avgHoursControls?.[rowIndex]?.get(month.toString()) as FormControl) ?? null;
  }

  public resetModifications(): void {
    this.loadFleetData();
  }

  public getExistingTruckSpecIds(): number[] {
    return this.dataSource.data.map((fleet) => fleet.truckSpec.truckSpecPk);
  }

  public addNewFleets(result: Array<Fleet>) {
    if (!result || result.length === 0) return;
    this.dataSource.data = [...(this.dataSource.data || []), ...result];
    this.buildForm();
    this.dataSource._updateChangeSubscription();
    this.isModificationsPending.update(() => true);
  }
}
